/**
 * Session-list (history row) avatar + line-1 helpers — aligned with iOS `ChatHistoryAPIMapper`
 * and Android `KusAvatarDisplayHelpers` (see `history-row-vs-thread-bubble-avatar-parity.md`).
 */

/**
 * Gravatar-style `d=blank` **query param** only (`?d=blank` / `&d=blank`), not path segments
 * like `/d=blank/`. Keep replace + "should load" detection aligned via the same pattern source.
 */
const GRAVATAR_D_BLANK_QUERY_SOURCE = '([?&])d=blank(?=(&|$))';
const GRAVATAR_STYLE_D_BLANK_PARAM = new RegExp(
  GRAVATAR_D_BLANK_QUERY_SOURCE,
  'gi',
);
const HAS_GRAVATAR_STYLE_D_BLANK_QUERY = new RegExp(
  GRAVATAR_D_BLANK_QUERY_SOURCE,
  'i',
);

export function normalizeAvatarUrlForSessionListLoad(
  url: string | undefined | null,
): string | undefined {
  if (url == null) return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(GRAVATAR_STYLE_D_BLANK_PARAM, '$1d=404');
}

export function shouldAttemptAvatarImageLoad(
  url: string | undefined | null,
): boolean {
  const trimmed = url?.trim() ?? '';
  if (!trimmed) return false;
  return !HAS_GRAVATAR_STYLE_D_BLANK_QUERY.test(trimmed);
}

export function resolveSessionListRowAvatarUrl({
  lastResponderAvatarUrl,
  secondaryResponderAvatarUrl,
  teamIconUrl,
}: {
  /** Prefer this when loadable (e.g. last agent message `sentBy.avatarUrl`). */
  lastResponderAvatarUrl?: string | null;
  /** Used when primary is missing or not loadable (e.g. `conversation.lastResponder.avatarUrl`). */
  secondaryResponderAvatarUrl?: string | null;
  teamIconUrl?: string | null;
}): string | undefined {
  const responderCandidates = [
    lastResponderAvatarUrl,
    secondaryResponderAvatarUrl,
  ];
  for (let i = 0; i < responderCandidates.length; i += 1) {
    const normalized = normalizeAvatarUrlForSessionListLoad(
      responderCandidates[i],
    );
    if (shouldAttemptAvatarImageLoad(normalized)) {
      return normalized;
    }
  }
  const normalizedTeam = normalizeAvatarUrlForSessionListLoad(teamIconUrl);
  if (shouldAttemptAvatarImageLoad(normalizedTeam)) {
    return normalizedTeam;
  }
  return undefined;
}

/**
 * Trim and strip leading `AI_` assistant prefix segments only (avoids corrupting names like `KAI_Smith`).
 */
export function normalizeAgentDisplayNameForSessionList(
  value: string | undefined | null,
): string {
  let s = (value ?? '').trim();
  while (s.startsWith('AI_')) {
    s = s.slice(3).trim();
  }
  return s;
}

/**
 * First character for session-list avatar initials (parity: iOS `AvatarView.getInitials`,
 * Android `KusAvatarView.setInitials` first code unit).
 */
export function sessionListInitialsLetter(
  userDisplayName: string | undefined | null,
): string {
  const agent = normalizeAgentDisplayNameForSessionList(userDisplayName);
  if (!agent) {
    return '';
  }
  const chars = Array.from(agent);
  if (chars.length === 0) {
    return '';
  }
  return chars[0].toLocaleUpperCase();
}

/**
 * Name used for session-list avatar initials (and image alt semantics).
 * Matches parity doc §2.1 / iOS history: **loadable** row image URL → prefer **agent**, then team;
 * **no** loadable row URL (`undefined` / non-loadable after Gravatar normalization) → prefer **team**, then agent.
 * Callers should pass the same string as `resolveSessionListRowAvatarUrl` (defense: we normalize here too).
 */
export function getSessionListAvatarUserDisplayName({
  resolvedAvatarUrl,
  agentDisplayName,
  teamName,
}: {
  resolvedAvatarUrl?: string | null;
  agentDisplayName?: string | null;
  teamName?: string | null;
}): string {
  const team = (teamName ?? '').trim();
  const agent = normalizeAgentDisplayNameForSessionList(agentDisplayName);
  const normalizedRowUrl =
    normalizeAvatarUrlForSessionListLoad(resolvedAvatarUrl);
  const hasLoadableRowImage =
    !!normalizedRowUrl && shouldAttemptAvatarImageLoad(normalizedRowUrl);

  if (hasLoadableRowImage) {
    if (agent) return agent;
    if (team) return team;
    return '';
  }
  if (team) return team;
  if (agent) return agent;
  return '';
}

function firstNonEmptyTrimmed(
  ...candidates: (string | undefined | null)[]
): string {
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (typeof c === 'string') {
      const t = c.trim();
      if (t) return t;
    }
  }
  return '';
}

export function getSessionListLine1Title({
  useLegacy,
  previewDetailsName,
  conversationName,
  agentDisplayName,
  teamName,
}: {
  useLegacy: boolean;
  previewDetailsName?: string | null;
  conversationName?: string | null;
  agentDisplayName?: string | null;
  teamName?: string | null;
}): string {
  const agent = normalizeAgentDisplayNameForSessionList(agentDisplayName);
  const team = (teamName ?? '').trim();
  if (useLegacy) {
    return firstNonEmptyTrimmed(agent, team);
  }
  return firstNonEmptyTrimmed(
    previewDetailsName,
    conversationName,
    agent,
    team,
  );
}
